import { memo, useMemo } from "react";
import Select, { MultiValue, SingleValue } from "react-select";
import { Controller, Control, FieldError } from "react-hook-form";

type Option = { value: string; label: string };

interface SelectInputProps {
  name: string;
  label?: string;
  control: Control<any>;
  options: Option[];
  error?: FieldError;
  isRequired?: boolean;
  placeholder?: string;
  disabled?: boolean;
  isMulti?: boolean;
}

function getSelectValue(
  value: string | undefined,
  options: Option[],
  isMulti?: boolean
): MultiValue<Option> | SingleValue<Option> {
  if (isMulti) {
    return (value ?? "")
      .split(",")
      .map((val: string) => options.find((option) => option.value === val))
      .filter(Boolean) as MultiValue<Option>;
  }
  return options.find((option) => option.value === value) || null;
}

function formatChangeForField(
  selected: MultiValue<Option> | SingleValue<Option>,
  isMulti?: boolean
): string {
  if (isMulti) {
    const multiSelected = selected as readonly Option[];
    return multiSelected?.map((opt) => opt.value).join(",") ?? "";
  }
  const singleSelected = selected as Option | null;
  return singleSelected?.value ?? "";
}

const getCustomStyles = (error?: FieldError) => ({
  control: (base: any, state: any) => ({
    ...base,
    borderColor: state.isFocused
      ? "#86b7fe"
      : error
      ? "#dc3545"
      : base.borderColor,
    boxShadow: state.isFocused
      ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
      : error
      ? "0 0 0 0.2rem rgba(220,53,69,.25)"
      : base.boxShadow,
    "&:hover": {
      borderColor: state.isFocused
        ? "#86b7fe"
        : error
        ? "#dc3545"
        : base.borderColor,
    },
    borderWidth: "1px",
    borderRadius: "0.375rem",
    minHeight: "38px",
    fontSize: "0.875rem",
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
});

function SelectInput({
  name,
  label = "",
  control,
  options,
  error,
  isRequired = false,
  placeholder = "",
  disabled,
  isMulti,
}: SelectInputProps) {
  const customStyles = useMemo(() => getCustomStyles(error), [error]);

  return (
    <div className="mb-3">
      <label className="form-label">
        {label}
        {isRequired && <span>*</span>}
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const selectValue = getSelectValue(field.value, options, isMulti);

          const handleChange = (selected: MultiValue<Option> | SingleValue<Option>) => {
            field.onChange(formatChangeForField(selected, isMulti));
          };

          return (
            <Select
              {...field}
              options={options}
              placeholder={placeholder}
              isClearable
              isMulti={isMulti}
              styles={customStyles}
              classNamePrefix="react-select"
              value={selectValue}
              onChange={handleChange}
              isDisabled={disabled}
            />
          );
        }}
      />
      {error && <div className="text-danger small mt-1">{error.message}</div>}
    </div>
  );
}

SelectInput.displayName = "SelectInput";

export default memo(SelectInput);
